import { motion } from 'framer-motion';
import ls from 'localstorage-slim';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../../api/instant-lease-api';
import { setLoaderModal } from '../../../../redux/cache-slice';
import {
  clearCarData,
  fetchLeaseApps,
} from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import { formatGermanPrice } from '../../../../utils/getter';
import CarImage from '../../../common/icons/hidden-car.svg?url';
import ButtonGroup from '../../molecules/button-group/button-group';
import './car-selection.css';

interface carSelectionProps {
  uuid: string;
  img?: string;
  carName?: string;
  color?: string;
  rate?: number | string;
  time?: string;
  cushion?: string;
  appLeaseDate?: string | Date | number | null;
  status?: string;
}

const CarSelection: React.FC<carSelectionProps> = ({
  img,
  uuid,
  carName = '',
  appLeaseDate = '',
  time = '',
  color = '',
  rate = '',
  cushion = '',
  status = '',
}: carSelectionProps) => {
  // Store
  const { leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Sequence Store
  const { sequence } = useSelector((state: RootState) => state.sequence);

  // Access Token
  const { accessToken } = useSelector((state: RootState) => state.auth);

  // History Hook
  const navigate = useNavigate();

  // Dispatch hook
  const dispatch = useAppDispatch();

  const formattedDate =
    appLeaseDate instanceof Date
      ? appLeaseDate.toLocaleDateString('de-DE')
      : appLeaseDate;

  // handle on click on leasing app
  const handleClick = (selectedUUID: string) => {
    const cancelApps = leaseApplications
      .filter((app) => app.uuid !== selectedUUID)
      .map((app) => app.uuid);
    const promises = cancelApps.map((leaseApp) => {
      return cancelLeaseApplication(accessToken, leaseApp);
    });
    Promise.all(promises)
      .then(() => {
        dispatch(
          setLoaderModal({
            visibility: true,
            text: 'Lade Fahrzeug- und Antragsdaten...',
          }),
        );
        dispatch(fetchLeaseApps({ accessToken }))
          .then(() => {
            dispatch(clearCarData());
            ls.remove('carData');
            ls.remove('ar');
            ls.remove('ps');
            ls.remove('bo');
            ls.remove('tr');
            ls.remove('loa');
            ls.remove('pcms');
            ls.remove('companyName');
            ls.remove('companyZip');
            navigate(`/lease-application/${selectedUUID}/${sequence + 1}`);
          })
          .catch((e) => {
            dispatch(
              setLoaderModal({
                visibility: false,
                text: 'Lade Fahrzeug- und Antragsdaten...',
              }),
            );
            console.error(e);
          });
      })
      .catch((e) => {
        dispatch(
          setLoaderModal({
            visibility: false,
            text: 'Lade Fahrzeug- und Antragsdaten...',
          }),
        );
        console.error(e);
      });
  };

  // Format price with german price format
  const { format } = formatGermanPrice();

  return (
    <motion.div
      whileHover='active'
      whileTap='clicked'
      animate='off'
      initial='off'
      className='car-selector'
    >
      <div className='car-price-image'>
        <img src={img || CarImage} alt='Car' />

        <span className='badge content-bold'>{status}</span>

        <div className='info'>
          <div>
            <span className='content-small'>
              Farbe: <span className='content-small-bold'>{color}</span>
            </span>
          </div>
          <div>
            <span className='content-small'>
              Polsterung: <span className='content-small-bold'>{cushion}</span>
            </span>
          </div>
        </div>
      </div>

      <div className='left car-selector-content'>
        <div className='car-selector-details'>
          <h1 className='title'>{carName}</h1>

          {appLeaseDate ? (
            <div className='car-date'>
              <span className='content-small'>Angebotsdatum: </span>
              <span className='content-small-bold car-date-value'>
                {formattedDate} | {time}
              </span>
            </div>
          ) : (
            'Noch Keinen Antrag angefangen'
          )}

          <div className='car-status-row'>
            <div className='car-status'>
              <span className='content-small'>Status: </span>
              <span className='content-small-bold'>{status}</span>
            </div>
            <span className='car-price'>
              <p className='price title'>{format(rate as any)}</p>{' '}
              <p className='car-text'>mtl.</p>
            </span>{' '}
          </div>
        </div>

        <ButtonGroup
          className='back-next'
          type='default'
          buttonOneProps={{
            type: 'submit',
            name: 'form-submit',
            className: 'button-text',
            dataTestId: 'Fahrzeug auswählen',
            children: 'Fahrzeug auswählen',
            onClick: () => handleClick(uuid),
          }}
        />
      </div>
    </motion.div>
  );
};

export default CarSelection;
